//
// Copyright (c) 2011-2018 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import { withTracking } from '@utils/withTracking';

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  imageUrl: string;
  actionLayer?: React.ReactElement;
  trackingId?: string;
}

/**
 * The BlurredMedia atom displays a blurred and darkened version of the given image in a container
 * whose dimensions must be set by the consumer. It accepts an `actionLayer` prop which will
 * render any provided components directly on top of the image container's bounding box.
 */
const BlurredMedia = ({
  actionLayer,
  className = '',
  imageUrl,
  onClick,
  trackingId = '',
}: Props) => {
  return (
    <MediaWrapper
      className={className}
      isClickable={!!onClick}
      onClick={onClick}
      trackingId={trackingId}
    >
      <Image src={imageUrl} />
      <MediaOverlay>{actionLayer}</MediaOverlay>
    </MediaWrapper>
  );
};

const MediaOverlay = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background: rgba(0, 0, 0, 0.3);
`;

const MediaWrapper = withTracking(
  styled.div<{
    isClickable: boolean;
  }>(
    ({ isClickable, theme }) => `
  background-color: ${theme.colors.white};
  cursor: ${isClickable ? 'pointer' : 'default'};
  position: relative;
  height: 100%;
  width: 100%;
  overflow: hidden;
`,
  ),
);

/**
 * Applying an 8px blur makes the inner 8px of the image slightly transparent,
 * so we scale the image by 8px on all sides and arrange it so that the blurred
 * section is cut off by our parent div with overflow hidden.
 */
const Image = styled.img(
  ({ theme }) => `
  filter: blur(${theme.spacings[2]});
  width: calc(100% + ${theme.spacings[3]});
  height: calc(100% + ${theme.spacings[3]});
  margin: -${theme.spacings[2]};
  object-fit: cover;
`,
);

export default BlurredMedia;
